import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, UploadCloud } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageTitle, ConfirmDelete } from "@/components/admin/ui";
import { MediaCard, UploadButton } from "@/components/admin/Media";
import { useMedia, uploadImage } from "@/lib/media";
import { cn } from "@/lib/utils";

export default function MediaLibrary() {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useMedia();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["media"] });
  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/media/${id}`),
    onSuccess: () => { toast.success("Image deleted"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const handleFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return toast.error("Please drop image files only.");
    setUploading(list.length);
    let ok = 0;
    for (const f of list) {
      try { await uploadImage(f); ok += 1; } catch (e) { toast.error(`${f.name}: ${formatApiError(e)}`); }
      setUploading((n) => n - 1);
    }
    if (ok) toast.success(ok > 1 ? `${ok} images uploaded` : "Image uploaded");
    invalidate();
  };

  const copy = async (url) => {
    const full = `${window.location.origin}${url}`;
    try { await navigator.clipboard.writeText(full); toast.success("Image URL copied"); } catch { toast.message(full); }
  };

  return (
    <div data-testid="admin-media-page">
      <PageTitle eyebrow="Content" title="Media Library">
        <UploadButton multiple testid="media-upload-button" onUploaded={() => invalidate()} className="btn-crimson !py-2.5">Upload images</UploadButton>
      </PageTitle>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        data-testid="media-dropzone"
        className={cn("mb-8 flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-12 text-center transition-colors", dragging ? "border-crimson bg-crimson/5" : "border-white/15 bg-surface/40")}
      >
        {uploading > 0 ? <Loader2 className="h-8 w-8 animate-spin text-crimson" /> : <UploadCloud className="h-8 w-8 text-slate-400" />}
        <p className="mt-4 font-display text-lg font-semibold">{uploading > 0 ? `Uploading ${uploading} image${uploading > 1 ? "s" : ""}…` : "Drag & drop photos here"}</p>
        <p className="mt-1 text-sm text-slate-500">JPG, PNG, WEBP or GIF · up to 10 MB each. Uploaded images can be used for vehicles, colours and the gallery.</p>
      </div>

      {isLoading ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div> : items.length === 0 ? (
        <p className="py-12 text-center text-slate-500" data-testid="media-empty">No uploads yet.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="media-grid">
          {items.map((m) => <MediaCard key={m.id} item={m} onCopy={() => copy(m.url)} onDelete={() => setPendingDelete(m)} />)}
        </div>
      )}

      {pendingDelete && (
        <ConfirmDelete
          open
          onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
          title={`Delete ${pendingDelete.filename}?`}
          description="Pages still using this image will show a broken picture until you replace it."
          onConfirm={() => { remove.mutate(pendingDelete.id); setPendingDelete(null); }}
          testid="media-delete"
        />
      )}
    </div>
  );
}
